import { getCohortSummary } from './cohortSheetSummary';
import {
  formatCacPaybackMonths,
  formatOpexMultiple,
  formatRunwayMonths,
  getUnderwritingNumericInputs,
  resolveModelNetBurn,
} from './underwritingSheetMetrics';
import {
  computeUaPredictionFromHill,
  formatUaPredictionRatio,
  resolveLatestCohortMarketingSpend,
} from './uaPrediction';

export const SCORECARD_KIND = {
  HIGHER: 'numeric_higher_better',
  LOWER: 'numeric_lower_better',
  QUALITATIVE: 'qualitative',
};

/** Metrics deferred until accounting snapshot or future QCF work. */
export const QCF_DEFERRED_METRIC_KEYS = new Set();

export const UW_SCORING_ROW_SPECS = [
  { row: 10, key: 'runway', metricLabel: 'Runway' },
  { row: 11, key: 'runoff_without_cash', metricLabel: 'Runoff' },
  { row: 12, key: 'liquid_value', metricLabel: 'Liquid Value' },
  { row: 13, key: 'revenue_growth_yoy', metricLabel: 'Revenue Growth (YoY)' },
  { row: 14, key: 'balance_sheet', metricLabel: 'Balance Sheet' },
  { row: 15, key: 'stress_testing', metricLabel: 'Stress Testing' },
  { row: 16, key: 'cash_balance', metricLabel: 'Cash Balance' },
  { row: 17, key: 'cac_payback', metricLabel: 'CAC Payback' },
  { row: 18, key: 'roas_vector', metricLabel: 'ROAS Vector' },
  { row: 19, key: 'ua_prediction', metricLabel: 'CAC Prediction' },
  { row: 20, key: 'investors', metricLabel: 'Investors' },
  { row: 21, key: 'management', metricLabel: 'Management' },
];

function snapshotNumeric(field) {
  const value = field?.value;
  return value != null && Number.isFinite(Number(value)) ? Number(value) : null;
}

function formatParamCurrency(value) {
  if (value == null || !Number.isFinite(Number(value))) {
    return null;
  }
  return `$${Math.round(Number(value)).toLocaleString('en-US')}`;
}

export function buildUwMetricParamsText(key, ctx) {
  const {
    beginCash = 0,
    monthlyOpex = 0,
    debt = 0,
    totalAssets = 0,
    totalLiabilities = 0,
    safe = 0,
    netBurn = 0,
    accountingSnapshot,
    cohortSummary,
    numericInputs,
    hillModel,
    referenceMarketingSpend,
  } = ctx;

  const parts = [];
  const pushCurrency = (label, value) => {
    const formatted = formatParamCurrency(value);
    if (formatted) {
      parts.push(`${label}=${formatted}`);
    }
  };
  const pushText = (label, value) => {
    if (value != null && value !== '') {
      parts.push(`${label}=${value}`);
    }
  };

  switch (key) {
    case 'runway': {
      if (ctx.runwaySource === 'Manual entry') {
        pushText('runway months', formatUwMetricValue('runway', numericInputs?.runway));
        break;
      }
      const cash = Number(beginCash) || 0;
      const netBurnValue =
        resolveModelNetBurn(netBurn) ?? snapshotNumeric(accountingSnapshot?.netBurn);
      pushCurrency('beginning cash', cash);
      if (netBurnValue != null) {
        pushText('net burn', `${formatParamCurrency(netBurnValue)}/mo`);
      }
      break;
    }
    case 'runoff_without_cash':
      pushCurrency(
        '12 month predicted runoff',
        numericInputs?.predicted_runoff_12_months ?? cohortSummary?.runoff12Months
      );
      pushCurrency('monthly OPEX', monthlyOpex);
      break;
    case 'liquid_value':
      pushCurrency('beginning cash', beginCash);
      pushCurrency(
        '12 month predicted runoff',
        numericInputs?.predicted_runoff_12_months ?? cohortSummary?.runoff12Months
      );
      pushCurrency('debt', debt);
      pushCurrency('monthly OPEX', monthlyOpex);
      break;
    case 'revenue_growth_yoy':
      if (accountingSnapshot?.revenueGrowthYoy?.reason) {
        return accountingSnapshot.revenueGrowthYoy.reason;
      }
      break;
    case 'balance_sheet': {
      const assets =
        numericInputs?.total_assets ??
        snapshotNumeric(accountingSnapshot?.totalAssets) ??
        (Number(totalAssets) > 0 ? Number(totalAssets) : null);
      const liabilities =
        numericInputs?.total_liabilities ??
        snapshotNumeric(accountingSnapshot?.totalLiabilities) ??
        (Number(totalLiabilities) > 0 ? Number(totalLiabilities) : null);
      const safeVal =
        snapshotNumeric(accountingSnapshot?.safe) ??
        (Number.isFinite(Number(safe)) ? Number(safe) : null);
      pushCurrency('total assets', assets);
      pushCurrency('total liabilities', liabilities);
      pushCurrency('SAFE', safeVal);
      break;
    }
    case 'cash_balance':
      pushCurrency('beginning cash', beginCash);
      pushCurrency('monthly OPEX', monthlyOpex);
      break;
    case 'cac_payback':
      pushText('ROAS threshold', '100%');
      if (numericInputs?.cac_payback != null) {
        pushText('first month at threshold', String(numericInputs.cac_payback));
      }
      break;
    case 'roas_vector':
      pushText('ROAS threshold', '50%');
      if (numericInputs?.roas_vector != null) {
        pushText('first month at threshold', String(numericInputs.roas_vector));
      }
      break;
    case 'ua_prediction': {
      const prediction =
        numericInputs?.ua_prediction_detail ??
        (hillModel && referenceMarketingSpend
          ? computeUaPredictionFromHill(hillModel, referenceMarketingSpend)
          : null);
      pushText(
        'target',
        prediction?.targetMob != null
          ? `100% cumulative ROAS at MOB ${prediction.targetMob}`
          : '100% cumulative ROAS at latest available MOB (capped at 12)'
      );
      pushCurrency('reference marketing', prediction?.referenceSpend);
      pushCurrency('max marketing at target', prediction?.maxSpend);
      break;
    }
    default:
      break;
  }

  return parts.length ? parts.join(', ') : null;
}

export function indexScorecardMetricsByKey(scorecardConfig) {
  const map = new Map();
  for (const metric of scorecardConfig?.metrics ?? []) {
    if (metric?.key) {
      map.set(metric.key, metric);
    }
  }
  return map;
}

export function scoreFromMetric(metric, numericValue) {
  if (!metric || metric.kind === SCORECARD_KIND.QUALITATIVE) {
    return null;
  }
  const value = Number(numericValue);
  if (!Number.isFinite(value)) {
    return null;
  }
  if (metric.kind === SCORECARD_KIND.HIGHER) {
    if (value >= Number(metric.thresholdScore3)) {
      return 3;
    }
    if (value >= Number(metric.thresholdScore2)) {
      return 2;
    }
    return 1;
  }
  if (metric.kind === SCORECARD_KIND.LOWER) {
    if (value < Number(metric.thresholdScore3)) {
      return 3;
    }
    if (value < Number(metric.thresholdScore2)) {
      return 2;
    }
    return 1;
  }
  return null;
}

export function scorecardScoreToRating(score) {
  if (score === 3) {
    return 'High';
  }
  if (score === 2) {
    return 'Moderate';
  }
  if (score === 1) {
    return 'Low';
  }
  return null;
}

/** Manual 1–3 score for qualitative UW rows; null when unset or invalid. */
export function normalizeQualitativeUwScore(value) {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return null;
  }
  const rounded = Math.round(n);
  if (rounded >= 1 && rounded <= 3) {
    return rounded;
  }
  return null;
}

function qualitativeUwScoreForKey(key, scores) {
  switch (key) {
    case 'investors':
      return normalizeQualitativeUwScore(scores.investorScore);
    case 'management':
      return normalizeQualitativeUwScore(scores.managementScore);
    case 'stress_testing':
      return normalizeQualitativeUwScore(scores.stressTestingScore);
    default:
      return null;
  }
}

function buildQualitativeScoringRow(spec, score, paramsText) {
  return {
    ...spec,
    valueText: String(score),
    rating: scorecardScoreToRating(score),
    score,
    paramsText,
  };
}

export const UW_SCORING_METRIC_COUNT = 12;

/** Mirrors Underwriting Calculator sheet totals (rows 22 and 25–28). */
export function computeUwScorecardTotals(scoringRows) {
  const rows = scoringRows ?? [];
  const ratedRows = rows.filter((row) => row.rating);
  const ratedCount = ratedRows.length;
  const equalWeightPercent = ratedCount > 0 ? 100 / ratedCount : null;

  const rowsWithWeights = rows.map((row) => {
    if (!row.rating || row.score == null || equalWeightPercent == null) {
      return { ...row, weightPercent: null, weightedScore: null };
    }
    const weightPercent = equalWeightPercent;
    const weightedScore = (row.score * weightPercent) / 100;
    return { ...row, weightPercent, weightedScore };
  });

  const totalWeightPercent = rowsWithWeights.reduce(
    (sum, row) => sum + (row.weightPercent ?? 0),
    0
  );
  const totalWeightedScore = rowsWithWeights.reduce(
    (sum, row) => sum + (row.weightedScore ?? 0),
    0
  );
  const maxPossibleWeightedScore =
    rowsWithWeights
      .filter((row) => row.score != null && row.score > 0)
      .reduce((sum, row) => sum + (row.weightPercent ?? 0), 0) *
    (3 / 100);

  const scorePercentOfMax =
    maxPossibleWeightedScore > 0
      ? totalWeightedScore / maxPossibleWeightedScore
      : null;

  let overallGrade = 'Enter Ratings';
  if (scorePercentOfMax != null) {
    if (scorePercentOfMax >= 0.75) {
      overallGrade = 'HIGH CONVICTION';
    } else if (scorePercentOfMax >= 0.5) {
      overallGrade = 'MODERATE';
    } else {
      overallGrade = 'LOW / PASS';
    }
  }

  return {
    rowsWithWeights,
    ratedCount,
    totalMetricCount: UW_SCORING_METRIC_COUNT,
    totalWeightPercent,
    totalWeightedScore,
    maxPossibleWeightedScore,
    scorePercentOfMax,
    overallGrade,
  };
}

export function formatUwMetricValue(key, numericValue) {
  if (numericValue == null || !Number.isFinite(Number(numericValue))) {
    return null;
  }
  const value = Number(numericValue);
  switch (key) {
    case 'runoff_without_cash':
      return formatRunwayMonths(value);
    case 'runway':
      return formatRunwayMonths(value);
    case 'cash_balance':
      return formatOpexMultiple(value);
    case 'liquid_value':
      return formatOpexMultiple(value);
    case 'cac_payback':
    case 'roas_vector':
      return formatCacPaybackMonths(value);
    case 'ua_prediction':
      return formatUaPredictionRatio(value);
    default:
      return String(Math.round(value * 100) / 100);
  }
}

export function buildUwScoringRows({
  cohortSheet,
  monthlyOpex,
  beginCash,
  debt,
  totalAssets = 0,
  totalAssetsSource = 'Default',
  totalLiabilities = 0,
  totalLiabilitiesSource = 'Default',
  safe = 0,
  netBurn = 0,
  runway = null,
  runwaySource = 'Default',
  predictedRunoff = null,
  predictedRunoffSource = 'Default',
  liquidValue = null,
  liquidValueSource = 'Default',
  cashBalance = null,
  cashBalanceSource = 'Default',
  balanceSheet = null,
  balanceSheetSource = 'Default',
  cacPredictionRatio = null,
  cacPredictionRatioSource = 'Default',
  revenueGrowthYoy = null,
  revenueGrowthYoySource = 'Default',
  lastMarketingSpend = 0,
  maxMarketingRoas100 = 0,
  hillModel = null,
  scorecardConfig,
  accountingSnapshot = null,
  investorScore = null,
  managementScore = null,
  stressTestingScore = null,
}) {
  if (!scorecardConfig?.metrics?.length) {
    return UW_SCORING_ROW_SPECS.map((spec) => ({
      ...spec,
      valueText: null,
      rating: null,
      score: null,
      paramsText: null,
    }));
  }

  const metricsByKey = indexScorecardMetricsByKey(scorecardConfig);
  const cohortSummary = getCohortSummary(cohortSheet);
  const referenceMarketingSpend = resolveLatestCohortMarketingSpend(cohortSheet);
  const numericInputs = getUnderwritingNumericInputs({
    cohortSheet,
    monthlyOpex,
    beginCash,
    debt,
    totalAssets,
    totalAssetsSource,
    totalLiabilities,
    totalLiabilitiesSource,
    safe,
    netBurn,
    runway,
    runwaySource,
    predictedRunoff,
    predictedRunoffSource,
    liquidValue,
    liquidValueSource,
    cashBalance,
    cashBalanceSource,
    balanceSheet,
    balanceSheetSource,
    cacPredictionRatio,
    cacPredictionRatioSource,
    revenueGrowthYoy,
    revenueGrowthYoySource,
    hillModel,
    lastMarketingSpend,
    maxMarketingRoas100,
    accountingSnapshot,
  });
  const paramsContext = {
    beginCash,
    monthlyOpex,
    debt,
    totalAssets,
    totalLiabilities,
    safe,
    netBurn,
    runwaySource,
    accountingSnapshot,
    cohortSummary,
    numericInputs,
    hillModel,
    referenceMarketingSpend,
  };
  const qualitativeScores = { investorScore, managementScore, stressTestingScore };

  return UW_SCORING_ROW_SPECS.map((spec) => {
    const paramsText = buildUwMetricParamsText(spec.key, paramsContext);
    const qualitativeScore = qualitativeUwScoreForKey(spec.key, qualitativeScores);
    if (qualitativeScore != null) {
      return buildQualitativeScoringRow(spec, qualitativeScore, paramsText);
    }
    if (QCF_DEFERRED_METRIC_KEYS.has(spec.key)) {
      return { ...spec, valueText: null, rating: null, score: null, paramsText };
    }
    const metric = metricsByKey.get(spec.key);
    if (!metric) {
      return { ...spec, valueText: null, rating: null, score: null, paramsText };
    }

    if (spec.key === 'ua_prediction') {
      const uaDetail = numericInputs.ua_prediction_detail;
      const referenceSpend = uaDetail?.referenceSpend ?? referenceMarketingSpend;
      if (!referenceSpend) {
        return { ...spec, valueText: null, rating: null, score: null, paramsText };
      }
      const numericValue = numericInputs.ua_prediction;
      if (numericValue == null) {
        if (hillModel && referenceSpend) {
          return {
            ...spec,
            valueText: '—',
            rating: scorecardScoreToRating(1),
            score: 1,
            paramsText,
          };
        }
        return { ...spec, valueText: null, rating: null, score: null, paramsText };
      }
      const score = scoreFromMetric(metric, numericValue);
      if (score == null) {
        return { ...spec, valueText: null, rating: null, score: null, paramsText };
      }
      return {
        ...spec,
        valueText: formatUwMetricValue(spec.key, numericValue),
        rating: scorecardScoreToRating(score),
        score,
        paramsText,
      };
    }

    const numericValue = numericInputs[spec.key];
    if (numericValue == null) {
      return { ...spec, valueText: null, rating: null, score: null, paramsText };
    }
    const score = scoreFromMetric(metric, numericValue);
    if (score == null) {
      return { ...spec, valueText: null, rating: null, score: null, paramsText };
    }
    return {
      ...spec,
      valueText: formatUwMetricValue(spec.key, numericValue),
      rating: scorecardScoreToRating(score),
      score,
      paramsText,
    };
  });
}
